import { useEffect, useState } from "react";
import { Play, FileDown, Globe } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

export default function Nav({ onPresent, onExport }) {
    const { t, lang, setLang, languages } = useI18n();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const [langOpen, setLangOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (!e.target.closest('[data-testid="lang-popover"]')) setLangOpen(false);
        };
        document.addEventListener("click", onClick);
        return () => document.removeEventListener("click", onClick);
    }, []);

    const links = [
        { id: "problem", label: t.nav.problem },
        { id: "solution", label: t.nav.solution },
        { id: "product", label: t.nav.product },
        { id: "metrics", label: t.nav.metrics },
        { id: "pricing", label: t.nav.pricing },
        { id: "team", label: t.nav.team },
    ];

    return (
        <header
            data-testid="main-nav"
            className={`no-print fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "backdrop-blur-xl bg-[#04140D]/75 border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-16 md:h-20 flex items-center justify-between">
                <a
                    href="#top"
                    data-testid="nav-logo"
                    className="flex items-center gap-2.5 group"
                >
                    <span className="h-9 w-9 rounded-lg bg-[#D4FF00] grid place-items-center text-[#04140D] font-display font-bold text-lg leading-none group-hover:rotate-3 transition-transform">
                        D
                    </span>
                    <span className="font-display font-bold text-lg tracking-tight">
                        Davini<span className="text-[#D4FF00]">.ai</span>
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-1">
                    {links.map((l) => (
                        <a
                            key={l.id}
                            href={`#${l.id}`}
                            data-testid={`nav-link-${l.id}`}
                            className="px-3 py-2 text-sm text-white/70 hover:text-[#D4FF00] transition-colors font-medium"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {/* Language switcher */}
                    <div className="relative" data-testid="lang-popover">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLangOpen((v) => !v);
                            }}
                            data-testid="lang-toggle"
                            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-full border border-white/15 hover:border-[#D4FF00]/50 hover:text-[#D4FF00] text-white/85 font-semibold text-xs uppercase tracking-widest transition-all"
                            title={t.langSwitcher.label}
                        >
                            <Globe size={14} />
                            <span>{lang.toUpperCase()}</span>
                        </button>
                        {langOpen && (
                            <div
                                data-testid="lang-menu"
                                className="absolute top-full right-0 mt-2 min-w-[160px] rounded-xl border border-white/10 bg-[#071B12] backdrop-blur-xl p-1 shadow-[0_24px_60px_-15px_rgba(0,0,0,0.7)]"
                            >
                                {languages.map((L) => (
                                    <button
                                        key={L.code}
                                        type="button"
                                        onClick={() => {
                                            setLang(L.code);
                                            setLangOpen(false);
                                        }}
                                        data-testid={`lang-option-${L.code}`}
                                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                                            lang === L.code
                                                ? "bg-[#D4FF00]/10 text-[#D4FF00]"
                                                : "text-white/85 hover:bg-white/[0.04]"
                                        }`}
                                    >
                                        <span>{L.name}</span>
                                        <span className="text-[10px] tracking-widest uppercase text-white/40 font-mono">
                                            {L.label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={onExport}
                        data-testid="nav-export-pdf"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full border border-white/15 hover:border-[#D4FF00]/50 hover:text-[#D4FF00] text-white/85 font-semibold text-xs uppercase tracking-widest transition-all"
                        title={t.nav.pdf}
                    >
                        <FileDown size={14} />
                        <span className="hidden md:inline">{t.nav.pdf}</span>
                    </button>
                    <button
                        type="button"
                        onClick={onPresent}
                        data-testid="nav-present"
                        className="hidden sm:inline-flex items-center gap-1.5 px-3.5 py-2.5 rounded-full bg-white/[0.06] border border-white/15 hover:border-[#D4FF00] hover:bg-[#D4FF00]/10 hover:text-[#D4FF00] text-white font-semibold text-xs uppercase tracking-widest transition-all"
                    >
                        <Play size={14} />
                        <span className="hidden md:inline">{t.nav.present}</span>
                    </button>
                    <a
                        href="#cta"
                        data-testid="nav-cta-button"
                        className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#D4FF00] hover:bg-[#BDF000] text-[#04140D] font-bold text-sm tracking-tight transition-all hover:scale-[1.03] active:scale-[0.98]"
                    >
                        {t.nav.demo}
                        <span aria-hidden>→</span>
                    </a>
                    <button
                        type="button"
                        aria-label="Toggle menu"
                        data-testid="nav-mobile-toggle"
                        className="lg:hidden h-10 w-10 grid place-items-center rounded-md border border-white/10"
                        onClick={() => setOpen((v) => !v)}
                    >
                        <span className="text-white">{open ? "✕" : "☰"}</span>
                    </button>
                </div>
            </div>
            {open && (
                <div className="lg:hidden border-t border-white/5 bg-[#04140D]/95 backdrop-blur-xl">
                    <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <a
                                key={l.id}
                                href={`#${l.id}`}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${l.id}`}
                                className="px-3 py-3 text-base text-white/85 hover:text-[#D4FF00] border-b border-white/5"
                            >
                                {l.label}
                            </a>
                        ))}
                        <div className="flex gap-2 pt-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onPresent?.();
                                }}
                                data-testid="mobile-present"
                                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-full bg-white/10 text-white font-semibold text-xs uppercase tracking-widest"
                            >
                                <Play size={14} /> {t.nav.present}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    onExport?.();
                                }}
                                data-testid="mobile-export"
                                className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-3 rounded-full border border-white/15 text-white/85 font-semibold text-xs uppercase tracking-widest"
                            >
                                <FileDown size={14} /> {t.nav.pdf}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
